/* ═══════════════════════════════════════════════════════════════════
   BLISS · POWER — Imperial Challenge Engine
   12 exercises grouped by 4 Animal Pillars.
   Each exercise has 3 joint-poses (Start / Mid / Finish) drawn as
   pure SVG gold-line silhouettes on a 100×60 viewport.
   Joints: head, neck, shL, elL, hnL, shR, elR, hnR, hip, knL, ftL, knR, ftR
   All coordinates are in the 0..100 × 0..60 canvas space.
   ═══════════════════════════════════════════════════════════════════ */

export const PILLARS = {
  Lion:     { key: 'lion',     color: '#C5A059', label: 'Lion',     desc: 'Upper-body Power'   },
  Stallion: { key: 'stallion', color: '#B8894A', label: 'Stallion', desc: 'Lower-body Strength'},
  Eagle:    { key: 'eagle',    color: '#D9C179', label: 'Eagle',    desc: 'Mobility & Reach'   },
  Leopard:  { key: 'leopard',  color: '#A67A2E', label: 'Leopard',  desc: 'Explosive Core'     },
};

// A shared "standing tall" reference for reuse
const STAND = {
  head:[50,10], neck:[50,17], shL:[46,18], elL:[45,26], hnL:[45,34], shR:[54,18], elR:[55,26], hnR:[55,34],
  hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55],
};

const clone = (o) => JSON.parse(JSON.stringify(o));
const with_ = (base, over) => ({ ...clone(base), ...over });

/* Utility: build the 4 phase image paths for an exercise id */
export const powerImgs = (id) => [
  `/api/uploads/power_exercises/${id}_start.png`,
  `/api/uploads/power_exercises/${id}_quarter.png`,
  `/api/uploads/power_exercises/${id}_half.png`,
  `/api/uploads/power_exercises/${id}_full.png`,
];

export const EXERCISES = [
  /* ─── LION (Upper body) ─── */
  {
    id: 'lion_push',
    name: 'Lion Power Push-Up',
    pillar: 'Lion',
    focus: 'Chest · Shoulders · Triceps',
    tip: 'Elbows tucked 45°. Full range. Explode up.',
    baseReps: { normal: 8, active: 15, athletic: 25 },
    seconds: 45,
    images: powerImgs('lion_push'),
    poses: [
      { head:[26,32], neck:[32,32], shL:[36,32], elL:[36,42], hnL:[36,52], shR:[36,30], elR:[36,42], hnR:[36,52],
        hip:[58,32], knL:[70,36], ftL:[86,52], knR:[70,34], ftR:[86,50] },
      { head:[26,44], neck:[32,42], shL:[36,42], elL:[42,50], hnL:[36,52], shR:[36,40], elR:[42,50], hnR:[36,52],
        hip:[58,40], knL:[70,44], ftL:[86,52], knR:[70,42], ftR:[86,50] },
      { head:[26,32], neck:[32,32], shL:[36,32], elL:[36,42], hnL:[36,52], shR:[36,30], elR:[36,42], hnR:[36,52],
        hip:[58,32], knL:[70,36], ftL:[86,52], knR:[70,34], ftR:[86,50] },
    ],
  },
  {
    id: 'lion_row',
    name: 'Lion Row',
    pillar: 'Lion',
    focus: 'Back · Biceps · Grip',
    tip: 'Squeeze shoulder blades. Elbows drive back, not up.',
    baseReps: { normal: 10, active: 15, athletic: 20 },
    seconds: 45,
    images: powerImgs('lion_row'),
    poses: [
      { head:[30,22], neck:[36,26], shL:[42,28], elL:[42,42], hnL:[42,52], shR:[42,26], elR:[42,42], hnR:[42,52],
        hip:[54,32], knL:[54,44], ftL:[54,54], knR:[54,44], ftR:[54,54] },
      { head:[30,22], neck:[36,26], shL:[42,28], elL:[54,26], hnL:[62,26], shR:[42,26], elR:[54,24], hnR:[62,24],
        hip:[54,32], knL:[54,44], ftL:[54,54], knR:[54,44], ftR:[54,54] },
      { head:[30,22], neck:[36,26], shL:[42,28], elL:[42,42], hnL:[42,52], shR:[42,26], elR:[42,42], hnR:[42,52],
        hip:[54,32], knL:[54,44], ftL:[54,54], knR:[54,44], ftR:[54,54] },
    ],
  },
  {
    id: 'lion_dip',
    name: 'Lion Tricep Dip',
    pillar: 'Lion',
    focus: 'Triceps · Chest',
    tip: 'Elbows straight back. Lower slow, drive up hard.',
    baseReps: { normal: 6, active: 12, athletic: 20 },
    seconds: 45,
    images: powerImgs('lion_dip'),
    poses: [
      { head:[50,14], neck:[50,20], shL:[46,22], elL:[42,32], hnL:[42,42], shR:[54,22], elR:[58,32], hnR:[58,42],
        hip:[50,32], knL:[46,44], ftL:[46,54], knR:[54,44], ftR:[54,54] },
      { head:[50,22], neck:[50,28], shL:[46,30], elL:[38,32], hnL:[42,42], shR:[54,30], elR:[62,32], hnR:[58,42],
        hip:[50,38], knL:[46,46], ftL:[46,54], knR:[54,46], ftR:[54,54] },
      { head:[50,14], neck:[50,20], shL:[46,22], elL:[42,32], hnL:[42,42], shR:[54,22], elR:[58,32], hnR:[58,42],
        hip:[50,32], knL:[46,44], ftL:[46,54], knR:[54,44], ftR:[54,54] },
    ],
  },

  /* ─── STALLION (Lower body) ─── */
  {
    id: 'stallion_squat',
    name: 'Stallion Squat',
    pillar: 'Stallion',
    focus: 'Quads · Glutes · Hamstrings',
    tip: 'Chest tall. Knees track over toes. Sit into hips.',
    baseReps: { normal: 15, active: 25, athletic: 40 },
    seconds: 45,
    images: powerImgs('stallion_squat'),
    poses: [
      with_(STAND, { hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] }),
      { head:[50,20], neck:[50,26], shL:[46,28], elL:[42,32], hnL:[38,36], shR:[54,28], elR:[58,32], hnR:[62,36],
        hip:[50,42], knL:[42,50], ftL:[46,56], knR:[58,50], ftR:[54,56] },
      with_(STAND, { hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] }),
    ],
  },
  {
    id: 'stallion_plank',
    name: 'Stallion Plank',
    pillar: 'Stallion',
    focus: 'Core · Shoulders · Glutes',
    tip: 'Body one straight line. Squeeze everything.',
    baseReps: { normal: 30, active: 45, athletic: 75 }, // seconds
    seconds: 45,
    images: powerImgs('stallion_plank'),
    poses: [
      { head:[24,32], neck:[30,32], shL:[36,32], elL:[36,42], hnL:[36,52], shR:[36,30], elR:[36,42], hnR:[36,52],
        hip:[60,32], knL:[72,32], ftL:[86,50], knR:[72,32], ftR:[86,50] },
      { head:[24,30], neck:[30,30], shL:[36,30], elL:[36,42], hnL:[36,52], shR:[36,28], elR:[36,42], hnR:[36,52],
        hip:[60,30], knL:[72,30], ftL:[86,48], knR:[72,30], ftR:[86,48] },
      { head:[24,32], neck:[30,32], shL:[36,32], elL:[36,42], hnL:[36,52], shR:[36,30], elR:[36,42], hnR:[36,52],
        hip:[60,32], knL:[72,32], ftL:[86,50], knR:[72,32], ftR:[86,50] },
    ],
  },
  {
    id: 'stallion_lunge',
    name: 'Stallion Lunge',
    pillar: 'Stallion',
    focus: 'Quads · Glutes · Balance',
    tip: 'Back knee kisses ground. Front shin vertical.',
    baseReps: { normal: 10, active: 16, athletic: 24 },
    seconds: 45,
    images: powerImgs('stallion_lunge'),
    poses: [
      with_(STAND, {}),
      { head:[50,20], neck:[50,26], shL:[46,28], elL:[45,36], hnL:[45,42], shR:[54,28], elR:[55,36], hnR:[55,42],
        hip:[50,38], knL:[36,48], ftL:[34,56], knR:[62,48], ftR:[66,56] },
      with_(STAND, {}),
    ],
  },

  /* ─── EAGLE (Mobility) ─── */
  {
    id: 'eagle_press',
    name: 'Eagle Press',
    pillar: 'Eagle',
    focus: 'Shoulders · Upper Back',
    tip: 'Rib cage down. Press through the ceiling.',
    baseReps: { normal: 10, active: 15, athletic: 20 },
    seconds: 45,
    images: powerImgs('eagle_press'),
    poses: [
      with_(STAND, { shL:[46,18], elL:[42,26], hnL:[42,32], shR:[54,18], elR:[58,26], hnR:[58,32] }),
      with_(STAND, { shL:[46,18], elL:[44,10], hnL:[44,2],  shR:[54,18], elR:[56,10], hnR:[56,2] }),
      with_(STAND, { shL:[46,18], elL:[42,26], hnL:[42,32], shR:[54,18], elR:[58,26], hnR:[58,32] }),
    ],
  },
  {
    id: 'eagle_cobra',
    name: 'Eagle Cobra Stretch',
    pillar: 'Eagle',
    focus: 'Spine · Chest · Hip Flexors',
    tip: 'Lift chest, open collarbones, breathe deep.',
    baseReps: { normal: 30, active: 40, athletic: 60 },
    seconds: 45,
    images: powerImgs('eagle_cobra'),
    poses: [
      { head:[26,44], neck:[32,42], shL:[38,42], elL:[38,50], hnL:[38,56], shR:[38,40], elR:[38,50], hnR:[38,56],
        hip:[58,52], knL:[70,54], ftL:[86,56], knR:[70,52], ftR:[86,54] },
      { head:[24,26], neck:[30,32], shL:[36,36], elL:[38,46], hnL:[38,56], shR:[36,34], elR:[38,46], hnR:[38,56],
        hip:[58,52], knL:[70,54], ftL:[86,56], knR:[70,52], ftR:[86,54] },
      { head:[26,44], neck:[32,42], shL:[38,42], elL:[38,50], hnL:[38,56], shR:[38,40], elR:[38,50], hnR:[38,56],
        hip:[58,52], knL:[70,54], ftL:[86,56], knR:[70,52], ftR:[86,54] },
    ],
  },
  {
    id: 'eagle_reach',
    name: 'Eagle Reach',
    pillar: 'Eagle',
    focus: 'Thoracic · Hips',
    tip: 'Opposite arm and leg. Balance and breathe.',
    baseReps: { normal: 12, active: 20, athletic: 30 },
    seconds: 45,
    images: powerImgs('eagle_reach'),
    poses: [
      { head:[26,32], neck:[32,32], shL:[38,32], elL:[38,42], hnL:[38,52], shR:[38,30], elR:[38,42], hnR:[38,52],
        hip:[58,32], knL:[70,32], ftL:[86,50], knR:[70,32], ftR:[86,50] },
      { head:[24,28], neck:[30,30], shL:[36,32], elL:[24,26], hnL:[14,20], shR:[36,30], elR:[36,42], hnR:[36,52],
        hip:[58,32], knL:[70,32], ftL:[86,50], knR:[76,26], ftR:[92,18] },
      { head:[26,32], neck:[32,32], shL:[38,32], elL:[38,42], hnL:[38,52], shR:[38,30], elR:[38,42], hnR:[38,52],
        hip:[58,32], knL:[70,32], ftL:[86,50], knR:[70,32], ftR:[86,50] },
    ],
  },

  /* ─── LEOPARD (Explosive core) ─── */
  {
    id: 'leopard_dash',
    name: 'Leopard Dash',
    pillar: 'Leopard',
    focus: 'Cardio · Legs · Coordination',
    tip: 'Drive knees high. Punch the air with elbows.',
    baseReps: { normal: 30, active: 45, athletic: 60 },
    seconds: 45,
    images: powerImgs('leopard_dash'),
    poses: [
      { head:[50,10], neck:[50,17], shL:[46,18], elL:[42,22], hnL:[38,18], shR:[54,18], elR:[58,26], hnR:[62,32],
        hip:[50,32], knL:[42,38], ftL:[38,44], knR:[54,44], ftR:[58,55] },
      with_(STAND, {}),
      { head:[50,10], neck:[50,17], shL:[46,18], elL:[50,26], hnL:[54,32], shR:[54,18], elR:[58,22], hnR:[62,18],
        hip:[50,32], knL:[46,44], ftL:[42,55], knR:[58,38], ftR:[62,44] },
    ],
  },
  {
    id: 'leopard_legraise',
    name: 'Leopard Leg Raise',
    pillar: 'Leopard',
    focus: 'Lower Abs · Hip Flexors',
    tip: 'Lower back stays flat. Control the descent.',
    baseReps: { normal: 12, active: 20, athletic: 30 },
    seconds: 45,
    images: powerImgs('leopard_legraise'),
    poses: [
      { head:[16,44], neck:[22,44], shL:[28,44], elL:[24,50], hnL:[20,56], shR:[28,42], elR:[24,50], hnR:[20,56],
        hip:[54,44], knL:[68,44], ftL:[86,44], knR:[68,44], ftR:[86,44] },
      { head:[16,44], neck:[22,44], shL:[28,44], elL:[24,50], hnL:[20,56], shR:[28,42], elR:[24,50], hnR:[20,56],
        hip:[54,44], knL:[62,32], ftL:[70,18], knR:[62,32], ftR:[70,18] },
      { head:[16,44], neck:[22,44], shL:[28,44], elL:[24,50], hnL:[20,56], shR:[28,42], elR:[24,50], hnR:[20,56],
        hip:[54,44], knL:[68,44], ftL:[86,44], knR:[68,44], ftR:[86,44] },
    ],
  },
  {
    id: 'leopard_bridge',
    name: 'Leopard Bridge',
    pillar: 'Leopard',
    focus: 'Glutes · Hamstrings · Core',
    tip: 'Squeeze glutes at the top. Ribs stay down.',
    baseReps: { normal: 15, active: 25, athletic: 40 },
    seconds: 45,
    images: powerImgs('leopard_bridge'),
    poses: [
      { head:[16,44], neck:[22,44], shL:[28,44], elL:[24,50], hnL:[20,56], shR:[28,42], elR:[24,50], hnR:[20,56],
        hip:[52,44], knL:[62,44], ftL:[68,52], knR:[62,44], ftR:[68,52] },
      { head:[16,44], neck:[22,44], shL:[28,44], elL:[24,50], hnL:[20,56], shR:[28,42], elR:[24,50], hnR:[20,56],
        hip:[52,30], knL:[64,32], ftL:[68,52], knR:[64,32], ftR:[68,52] },
      { head:[16,44], neck:[22,44], shL:[28,44], elL:[24,50], hnL:[20,56], shR:[28,42], elR:[24,50], hnR:[20,56],
        hip:[52,44], knL:[62,44], ftL:[68,52], knR:[62,44], ftR:[68,52] },
    ],
  },

  /* ─── NEW · Lion Pull-Up · vertical pull (back / biceps) ─── */
  {
    id: 'lion_pullup',
    name: 'Lion Pull-Up',
    pillar: 'Lion',
    focus: 'Back Width · Biceps · Grip',
    tip: 'Full hang start. Chin clears bar. No swing.',
    baseReps: { normal: 3, active: 8, athletic: 15 },
    seconds: 45,
    images: [
      '/power_exercises/lion_pullup_start.png',
      '/power_exercises/lion_pullup_start.png',
      '/power_exercises/lion_pullup_start.png',
      '/power_exercises/lion_pullup_start.png',
    ],
    poses: [
      // Dead hang: arms fully extended overhead, body long
      { head:[50,18], neck:[50,20], shL:[46,22], elL:[46,14], hnL:[46, 6], shR:[54,22], elR:[54,14], hnR:[54, 6],
        hip:[50,36], knL:[47,46], ftL:[47,56], knR:[53,46], ftR:[53,56] },
      // Top: chin over bar, elbows bent
      { head:[50, 8], neck:[50,12], shL:[42,14], elL:[38,10], hnL:[42, 6], shR:[58,14], elR:[62,10], hnR:[58, 6],
        hip:[50,28], knL:[47,40], ftL:[47,52], knR:[53,40], ftR:[53,52] },
      // Back to dead hang
      { head:[50,18], neck:[50,20], shL:[46,22], elL:[46,14], hnL:[46, 6], shR:[54,22], elR:[54,14], hnR:[54, 6],
        hip:[50,36], knL:[47,46], ftL:[47,56], knR:[53,46], ftR:[53,56] },
    ],
  },

  /* ─── NEW · Stallion Deadlift · hip hinge (hamstrings / glutes) ─── */
  {
    id: 'stallion_deadlift',
    name: 'Stallion Deadlift',
    pillar: 'Stallion',
    focus: 'Hamstrings · Glutes · Lower Back',
    tip: 'Hinge, don\'t squat. Bar close to shins. Neutral spine.',
    baseReps: { normal: 6, active: 12, athletic: 18 },
    seconds: 45,
    images: [
      '/power_exercises/stallion_deadlift_start.png',
      '/power_exercises/stallion_deadlift_start.png',
      '/power_exercises/stallion_deadlift_start.png',
      '/power_exercises/stallion_deadlift_start.png',
    ],
    poses: [
      // Standing tall, arms hanging (bar at hip)
      { head:[50,10], neck:[50,17], shL:[46,18], elL:[45,26], hnL:[45,34], shR:[54,18], elR:[55,26], hnR:[55,34],
        hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] },
      // Hinged forward, torso ~45°, arms hanging straight down
      { head:[38,20], neck:[42,22], shL:[42,26], elL:[42,36], hnL:[42,44], shR:[46,26], elR:[46,36], hnR:[46,44],
        hip:[54,32], knL:[52,44], ftL:[52,55], knR:[56,44], ftR:[56,55] },
      // Back to stand tall
      { head:[50,10], neck:[50,17], shL:[46,18], elL:[45,26], hnL:[45,34], shR:[54,18], elR:[55,26], hnR:[55,34],
        hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] },
    ],
  },

  /* ─── NEW · Eagle Rotation · anti-rotation core (obliques) ─── */
  {
    id: 'eagle_rotation',
    name: 'Eagle Rotation',
    pillar: 'Eagle',
    focus: 'Obliques · Anti-Rotation Core · Spine',
    tip: 'Hips still. Rotate through the ribs, not the arms.',
    baseReps: { normal: 8, active: 14, athletic: 22 },
    seconds: 45,
    images: [
      '/power_exercises/eagle_rotation_start.png',
      '/power_exercises/eagle_rotation_start.png',
      '/power_exercises/eagle_rotation_start.png',
      '/power_exercises/eagle_rotation_start.png',
    ],
    poses: [
      // Standing, arms extended forward, ball at chest
      { head:[50,10], neck:[50,17], shL:[46,18], elL:[44,24], hnL:[42,30], shR:[54,18], elR:[56,24], hnR:[58,30],
        hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] },
      // Rotate right
      { head:[52,10], neck:[52,17], shL:[48,18], elL:[54,22], hnL:[62,26], shR:[56,18], elR:[62,22], hnR:[70,26],
        hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] },
      // Rotate left
      { head:[48,10], neck:[48,17], shL:[52,18], elL:[46,22], hnL:[38,26], shR:[44,18], elR:[38,22], hnR:[30,26],
        hip:[50,32], knL:[47,44], ftL:[47,55], knR:[53,44], ftR:[53,55] },
    ],
  },
];

/* Exercise demo videos (made per exercise id) — clipReps = repetitions inside one loop. */
const VIDEOS = {
  lion_push: 2, lion_row: 1, lion_dip: 1, stallion_squat: 1, stallion_lunge: 1,
  eagle_press: 1, leopard_bridge: 1, stallion_plank: 1, eagle_cobra: 1, eagle_reach: 1,
  leopard_dash: 1, leopard_legraise: 2, lion_pullup: 1, stallion_deadlift: 1, eagle_rotation: 1,
};
EXERCISES.forEach((e) => {
  if (VIDEOS[e.id]) { e.video = `/api/uploads/power_exercises/${e.id}.mp4`; e.clipReps = VIDEOS[e.id]; }
});

/* ─── Progression rule ─── */
export const PROGRESSION = [
  { weeks: 'Week 1-2',  focus: 'Form Calibration & Stamina',      level: 1 },
  { weeks: 'Week 3-4',  focus: 'Strength Accumulation',           level: 2 },
  { weeks: 'Month 2+',  focus: 'Power & Imperial Mastery',        level: 3 },
];

export const DURATIONS = [
  { key: 'w1',   label: '1 Week',   days: 7,   tier: 'Short-term' },
  { key: 'w2',   label: '2 Weeks',  days: 14,  tier: 'Short-term' },
  { key: 'w3',   label: '3 Weeks',  days: 21,  tier: 'Short-term' },
  { key: 'm1',   label: '1 Month',  days: 30,  tier: 'Long-term'  },
  { key: 'm2',   label: '2 Months', days: 60,  tier: 'Long-term'  },
  { key: 'm3',   label: '3 Months', days: 90,  tier: 'Long-term'  },
  { key: 'd100', label: '100-Day Imperial Legacy', days: 100, tier: 'Ultimate' },
];

/* Fixed 7-day full-body schedule (loops modulo 7 across weeks/months).
   Day mapping (index → day number):
     0 = Day 1: Upper Body Power
     1 = Day 2: Lower Body & Glutes
     2 = Day 3: Core, Stability & Cardio
     3 = Day 4: Active Recovery & Mobility
     4 = Day 5: Full Body Power Integration
     5 = Day 6: Endurance & Core Blast
     6 = Day 7: Rest
*/
const WEEKLY_SCHEDULE = [
  ['lion_push', 'lion_pullup', 'lion_row', 'lion_dip', 'eagle_press'],
  ['stallion_squat', 'stallion_lunge', 'stallion_deadlift', 'leopard_bridge'],
  ['stallion_plank', 'leopard_legraise', 'eagle_rotation', 'leopard_dash'],
  ['eagle_cobra', 'eagle_reach'],
  ['lion_pullup', 'stallion_squat', 'lion_push', 'stallion_deadlift', 'eagle_rotation'],
  ['leopard_dash', 'leopard_legraise', 'lion_dip', 'stallion_plank'],
  [], // Day 7 — Rest
];

export function pickDayExercises(dayIndex, intensity) {
  void intensity;
  return WEEKLY_SCHEDULE[dayIndex % 7];
}

/* Rest days: Day 7 of every week is rest across all intensities. */
export function isRestDay(dayIndex, intensity) {
  void intensity;
  return (dayIndex % 7) === 6;
}

/* ── Personal targets: level (Normal/Active/Athletic) × gender × week growth ── */
const HOLDS = new Set(['stallion_plank', 'eagle_cobra', 'leopard_dash']);
const UPPER = new Set(['lion_push', 'lion_row', 'lion_dip', 'lion_pullup', 'eagle_press']);
const HEAVY = new Set(['lion_pullup', 'stallion_deadlift', 'lion_dip']);
const SEC_PER_REP = { normal: 3.5, active: 3, athletic: 2.5 };
const levelOf = (plan) => (SEC_PER_REP[plan?.intensity] ? plan.intensity : 'normal');

export const isHoldEx = (ex) => (ex?.isCustom ? ex.unit === 'seconds' : HOLDS.has(ex?.id));

export const blissTarget = (ex, plan, dayIndex = 0) => {
  if (!ex) return 10;
  if (ex.isCustom || !ex.baseReps) return ex.target || ex.baseReps?.normal || 15;
  const g = plan?.gender === 'female' ? (UPPER.has(ex.id) ? 0.8 : 0.9) : 1;
  const week = Math.floor(dayIndex / 7) + 1;
  const grow = week <= 2 ? 1 : week <= 4 ? 1.15 : 1.3;
  const v = ex.baseReps[levelOf(plan)] * g * grow;
  return HOLDS.has(ex.id) ? Math.max(10, Math.round(v / 5) * 5) : Math.max(1, Math.round(v));
};

export const timerSecondsFor = (ex, reps, plan) => {
  if (!ex) return 45;
  if (ex.isCustom || !ex.baseReps) return ex.seconds || 45;
  if (HOLDS.has(ex.id)) return reps;
  const per = SEC_PER_REP[levelOf(plan)] * (HEAVY.has(ex.id) ? 1.5 : 1);
  return Math.max(20, Math.ceil((reps * per) / 5) * 5);
};
